import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Wallet, PauseCircle } from "lucide-react";

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function SpendLimit() {
  const [data, setData] = useState(null);
  const [cap, setCap] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/bank/spend").then((r) => { setData(r.data); setCap((c) => (c === "" ? String(r.data.cap_usd) : c)); });
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, []);

  const save = async () => {
    const v = Number(cap);
    if (!Number.isFinite(v) || v < 0 || v > 1000) { setMsg("Enter an amount between $0 and $1,000."); return; }
    setSaving(true); setMsg("");
    try {
      const r = await api.put("/admin/bank/spend", { cap_usd: v });
      setData(r.data); setCap(String(r.data.cap_usd)); setMsg("Saved");
    } catch { setMsg("Couldn't save. Try again."); } finally { setSaving(false); }
  };

  if (!data) return <div className="pm-card p-5 mb-6 flex justify-center" data-testid="spend-loading"><Loader2 className="animate-spin" /></div>;
  const pct = data.cap_usd > 0 ? Math.min(100, (data.background_usd / data.cap_usd) * 100) : 100;
  const over = data.background_usd >= data.cap_usd;

  return (
    <div className="pm-card p-5 mb-6" data-testid="spend-limit-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-display font-bold flex items-center gap-2"><Wallet size={15} /> Daily AI spend · background filling</div>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Estimated from text length and typical prices, not your provider bill. Resets at midnight UTC.</p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs" style={{ color: "var(--text-muted)" }}>
            Daily limit ($)
            <input type="number" min="0" max="1000" step="1" value={cap} onChange={(e) => { setCap(e.target.value); setMsg(""); }}
              className="block mt-1 w-28 px-3 py-2 rounded-lg border font-mono text-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="spend-cap-input" />
          </label>
          <button onClick={save} disabled={saving} className="pm-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="spend-cap-save">
            {saving && <Loader2 size={13} className="animate-spin" />} Save
          </button>
        </div>
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-mono text-2xl font-bold" style={{ color: over ? "var(--weakness)" : undefined }} data-testid="spend-today">{usd(data.background_usd)}</span>
        <span className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="spend-cap-display">of {usd(data.cap_usd)} today</span>
        {msg && <span className="text-xs ml-auto" style={{ color: msg === "Saved" ? "var(--mastery)" : "var(--weakness)" }} data-testid="spend-cap-msg">{msg}</span>}
      </div>
      <div className="h-2 rounded-full mt-2 overflow-hidden" style={{ background: "var(--muted)" }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: over ? "var(--weakness)" : "var(--primary)", transition: "width 0.4s ease" }} data-testid="spend-bar" />
      </div>
      <div className="text-xs mt-3 flex flex-wrap gap-x-4 gap-y-1" style={{ color: "var(--text-muted)" }}>
        <span data-testid="spend-user">Student requests today: {usd(data.user_usd)} (not limited)</span>
        <span>Last fill: {data.last_fill_at ? new Date(data.last_fill_at).toLocaleString() : "—"}</span>
        {data.paused_until && (
          <span className="inline-flex items-center gap-1" style={{ color: "var(--weakness)" }} data-testid="spend-paused">
            <PauseCircle size={12} /> AI paused after a limit error until {new Date(data.paused_until).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
}
